import React from 'react';
import { motion } from 'framer-motion';
import { Target, Eye, Award, Users } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const About = () => {
  const values = [
    { icon: Target, title: 'Creativity', description: 'Innovative concepts that captivate and inspire' },
    { icon: Award, title: 'Professionalism', description: 'Unwavering commitment to excellence in every detail' },
    { icon: Eye, title: 'Innovation', description: 'Cutting-edge solutions for modern event challenges' },
    { icon: Users, title: 'Client-Centricity', description: 'Your vision at the heart of everything we do' },
  ];

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="about-page">
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#D4AF37] mb-4 block">
              Who We Are
            </span>
            <h1 className="text-5xl md:text-7xl font-medium leading-[0.9] mb-6 tracking-tight">
              Crafting Experiences.
              <br />
              <span className="text-gradient">Elevating Brands.</span>
            </h1>
            <p className="text-lg md:text-xl font-light leading-relaxed text-white/70 max-w-3xl mb-8">
              Nehpets+ Concepts is a premium event consultancy that transforms strategic objectives into 
              extraordinary experiences. We operate at the intersection of creative vision and flawless 
              execution—designing, planning, and delivering events that reflect the highest standards 
              of our corporate, government, and private clients.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Story */}
      <section className="py-20 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <h2 className="text-4xl md:text-5xl font-normal leading-tight">Our Story</h2>
              <div className="space-y-4 text-white/70 leading-relaxed">
                <p>
                  Founded on December 14th, 2007, Nehpets+ Concepts was born from a passion for creating
                  meaningful connections through exceptional events. Over a decade of dedicated service, 
                  we've evolved from a boutique event planning service to a comprehensive brand experience company.
                </p>
                <p>
                  Our journey has taken us across diverse sectors - from intimate corporate retreats to
                  grand government ceremonies, from creative brand activations to elegant wedding celebrations.
                  Each project has strengthened our expertise and deepened our commitment to excellence.
                </p>
                <p>
                  Today, we stand as a trusted partner for organizations and individuals who demand nothing
                  but the best. Our track record speaks volumes: 500+ successful events, 200+ satisfied clients,
                  and countless memories that last a lifetime.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img
                src="https://images.unsplash.com/photo-1758518731468-98e90ffd7430"
                alt="Nehpets+ Team"
                className="w-full aspect-[4/3] object-cover border border-white/10"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="py-20 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-[#0A0A0F] border border-white/5 p-10 space-y-4"
            >
              <Eye className="text-[#D4AF37]" size={40} />
              <h3 className="text-3xl font-medium">Our Vision</h3>
              <p className="text-white/70 leading-relaxed">
                To be a leading African events and creative solutions company that transforms ideas into
                remarkable experiences and memorable brand moments.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-[#0A0A0F] border border-white/5 p-10 space-y-4"
            >
              <Target className="text-[#D4AF37]" size={40} />
              <h3 className="text-3xl font-medium">Our Mission</h3>
              <p className="text-white/70 leading-relaxed">
                To design, plan, and execute world-class events through innovation, strategic thinking,
                and seamless coordination, while providing clients with powerful branding and creative
                solutions that elevate their identity.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-16 text-center"
          >
            <h2 className="text-4xl md:text-5xl font-normal leading-tight mb-6">Our Core Values</h2>
            <p className="text-lg font-light leading-relaxed text-white/70 max-w-2xl mx-auto">
              The principles that guide every decision we make and every event we create
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center space-y-4"
              >
                <div className="flex justify-center">
                  <div className="p-4 border border-[#D4AF37]/30 bg-[#D4AF37]/5">
                    <value.icon className="text-[#D4AF37]" size={32} />
                  </div>
                </div>
                <h3 className="text-xl font-semibold">{value.title}</h3>
                <p className="text-white/60 text-sm leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Approach */}
      <section className="py-20 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-16 text-center"
          >
            <h2 className="text-4xl md:text-5xl font-normal leading-tight mb-6">Our Approach</h2>
            <p className="text-lg font-light leading-relaxed text-white/70 max-w-2xl mx-auto">
              A proven methodology that delivers exceptional results, every time
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
            {[
              { step: '01', title: 'Discovery', description: 'Understanding your vision and objectives' },
              { step: '02', title: 'Concept', description: 'Developing creative concepts and strategies' },
              { step: '03', title: 'Planning', description: 'Detailed planning and pre-production' },
              { step: '04', title: 'Execution', description: 'Flawless event management and coordination' },
              { step: '05', title: 'Review', description: 'Post-event analysis and insights' },
            ].map((phase, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center space-y-3"
              >
                <div className="text-4xl font-bold text-[#D4AF37]/30">{phase.step}</div>
                <h3 className="text-lg font-semibold">{phase.title}</h3>
                <p className="text-white/60 text-sm leading-relaxed">{phase.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default About;